<!--
    MvsReviewWorkbenchComponent — the shared "Review Workbench" layout (fe_core_review-workbench).

    A single PrimeNG <p-splitter> is the SOLE layout owner and BOTH/ALL panes are ALWAYS mounted. Two
    static branches (2- vs 3-pane) keep each <p-splitter> with a fixed number of <ng-template pTemplate>
    panels — mirroring the proven ei-review-workbench (3) / payment-reconciliation (2) usages — so panel
    discovery never depends on a conditional template. Consumers project each pane's CONTENT via NAMED
    templates (#workbenchList / #workbenchDetail / + optional #workbenchCenter) which we render with
    *ngTemplateOutlet. This is DELIBERATE: <ng-content> does NOT project into an <ng-template pTemplate>
    embedded view (the projected nodes never appear → empty panes), whereas *ngTemplateOutlet DOES
    instantiate inside an embedded view. Each outlet host is the flex child of .rw-col and owns its own
    scroll. The ::ng-deep .p-splitter-panel flex chain is owned here.

    HEIGHT: never set in this template — see the HEIGHT CONTRACT in the component's class TSDoc.

    COLLAPSE: the `rw-collapse-*` / `rw-collapsible` classes are the ONLY thing `collapsePane` changes.
    Collapsing is a frequent user interaction, so it must never touch the STRUCTURE — no *ngIf, no moving
    content between slots, no extra splitter branch — or the projected embedded views would be destroyed and
    the user's in-progress input with them. All four class bindings are false for a consumer that does not
    use the feature, so no class is emitted and the markup is unchanged.

    STACKING: `rw-stacked` is the same kind of binding and the same promise — the ONLY thing
    `stackBelowLg` changes. Crossing the breakpoint is a resize, i.e. something that can happen while the
    user is half-way through an input, so it must not touch the structure either. See the `stackBelowLg`
    input for why this is CSS and not PrimeNG's own `layout`.

    FIXED SPLIT: `rw-no-resize` likewise. Note what it does NOT do — it does not remove the gutter. The
    gutter stays a flex item and becomes the GAP between the two panes, because it is the only thing
    separating them and because the collapse rules already remove the one next to a collapsed pane. See
    the `resizable` input; the class is emitted only for a consumer that binds `[resizable]="false"`.
-->
@if (panes === 3) {
    <div class="rw-splitter-wrap flex-grow-1 overflow-hidden"
         [class.rw-stacked]="stackBelowLg"
         [class.rw-no-resize]="fixedSplit"
         [class.rw-collapsible]="collapsible"
         [class.rw-collapse-list]="collapseList"
         [class.rw-collapse-detail]="collapseDetail">
        <p-splitter [panelSizes]="resolvedPanelSizes" [minSizes]="resolvedMinSizes" styleClass="h-full">
            <ng-template pTemplate>
                <div class="rw-col"><ng-container *ngTemplateOutlet="listTpl"></ng-container></div>
            </ng-template>
            <ng-template pTemplate>
                <div class="rw-col"><ng-container *ngTemplateOutlet="centerTpl"></ng-container></div>
            </ng-template>
            <ng-template pTemplate>
                <div class="rw-col"><ng-container *ngTemplateOutlet="detailTpl"></ng-container></div>
            </ng-template>
        </p-splitter>
    </div>
} @else {
    <!--
        Serves BOTH panes === 2 (default) and panes === 1. They share one structure on purpose: PrimeNG's
        Splitter derives its panel list once in onAfterContentInit and never re-reads its pTemplate
        children, so a splitter can never gain or lose a pane. Giving 1-pane its own branch would destroy
        and re-create the splitter — and with it the projected #workbenchList view — on every toggle,
        re-mounting and re-fetching the worklist table. `rw-single` instead collapses the detail pane and
        the gutter, leaving the worklist DOM untouched. See the `panes` input for the full rationale.
        For panes === 2 the class binding is false, so the markup is unchanged.
    -->
    <div class="rw-splitter-wrap flex-grow-1 overflow-hidden"
         [class.rw-single]="singlePane"
         [class.rw-stacked]="stackBelowLg"
         [class.rw-no-resize]="fixedSplit"
         [class.rw-collapsible]="collapsible"
         [class.rw-collapse-list]="collapseList"
         [class.rw-collapse-detail]="collapseDetail">
        <p-splitter [panelSizes]="resolvedPanelSizes" [minSizes]="resolvedMinSizes" styleClass="h-full">
            <ng-template pTemplate>
                <div class="rw-col"><ng-container *ngTemplateOutlet="listTpl"></ng-container></div>
            </ng-template>
            <ng-template pTemplate>
                <div class="rw-col"><ng-container *ngTemplateOutlet="detailTpl"></ng-container></div>
            </ng-template>
        </p-splitter>
    </div>
}
